import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const normalizeSSHKey = (privateKey) => {
  if (!privateKey || privateKey.trim() === '') {
    return privateKey;
  }
  
  return privateKey
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
};

export const addNode = async ({
  addressDetails,
  nodeType,
  sudoUserCreds,
  clientId,
  departmentIds = [],
  teamIds = [],
  userIds = [],
  token
}) => {
  try {
    const requestData = {
      client_id: clientId,
      department_ids: departmentIds,
      team_ids: teamIds,
      user_ids: userIds,
      address_details: {
        ip_address: addressDetails.ipAddress,
        port: parseInt(addressDetails.port),
        region: addressDetails.region
      },
      node_type: nodeType,
      sudo_user_creds: {
        username: sudoUserCreds.username,
        password: sudoUserCreds.password,
        private_key: normalizeSSHKey(sudoUserCreds.privateKey)
      }
    };

    console.log('Adding node with data:', requestData);

    const response = await axios.post(`${BASE_URL}/compute-v2/v2/node`, requestData, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: { id: response.data.id },
      status: response.status,
      message: 'Node added successfully'
    };
  } catch (error) {
    console.error('Add Node API Error:', error);
    return handleError(error);
  }
};

export const getNodeById = async (id, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/compute-v2/v2/node/${id}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: response.data.node,
      status: response.status,
      message: 'Node retrieved successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getNodes = async ({
  paginationParams = {},
  sortingParams = {},
  clientId,
  departmentId,
  teamId,
  userId,
  token
}) => {
  try {
    const params = new URLSearchParams();
    
    if (paginationParams.page !== undefined) params.append('pagination_params.page', paginationParams.page);
    if (paginationParams.page_size !== undefined) params.append('pagination_params.page_size', paginationParams.page_size);
    if (paginationParams.limit !== undefined) params.append('pagination_params.page_size', paginationParams.limit);
    if (sortingParams.sortBy) params.append('sort_by', sortingParams.sortBy);
    if (sortingParams.sortOrder) params.append('sort_order', sortingParams.sortOrder);
    if (clientId) params.append('client_id', clientId);
    if (departmentId) params.append('department_id', departmentId);
    if (teamId) params.append('team_id', teamId);
    if (userId) params.append('user_id', userId);

    const response = await axios.get(`${BASE_URL}/compute-v2/v2/nodes?${params.toString()}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: {
        nodes: response.data.nodes || [],
        paginationResponse: response.data.pagination_response || {}
      },
      status: response.status,
      message: 'Nodes retrieved successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getNodeSSHDetailsById = async (id, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/compute-v2/v2/node/${id}/ssh`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: {
        addressDetails: response.data.address_details,
        sudoUserCreds: response.data.sudo_user_creds
      },
      status: response.status,
      message: 'SSH details retrieved successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateNode = async ({
  id,
  clientId,
  departmentIds = [],
  teamIds = [],
  userIds = [],
  addressDetails,
  nodeType,
  sudoUserCreds,
  token
}) => {
  try {
    const requestData = {
      client_id: clientId,
      department_ids: departmentIds,
      team_ids: teamIds,
      user_ids: userIds,
      address_details: addressDetails ? {
        ip_address: addressDetails.ipAddress,
        port: parseInt(addressDetails.port),
        region: addressDetails.region
      } : undefined,
      node_type: nodeType,
      sudo_user_creds: sudoUserCreds ? {
        username: sudoUserCreds.username,
        password: sudoUserCreds.password,
        private_key: normalizeSSHKey(sudoUserCreds.privateKey)
      } : undefined
    };

    Object.keys(requestData).forEach(key => 
      requestData[key] === undefined && delete requestData[key]
    );

    const response = await axios.patch(
      `${BASE_URL}/compute-v2/v2/node/${id}`, 
      requestData,
      { 
        headers: { 
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
          'x-auth-user': token
        }
      }
    );

    return {
      success: true,
      data: response.data.node,
      status: response.status,
      message: 'Node updated successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteNode = async (id, token) => {
  try {
    const response = await axios.delete(`${BASE_URL}/compute-v2/v2/node/${id}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      status: response.status,
      message: 'Node deleted successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const NodeType = {
  NODE_TYPE_UNSPECIFIED: 0,
  NODE_TYPE_STORAGE: 1,
  NODE_TYPE_COMPUTE: 2
};

export const NodeStatus = {
  NODE_STATUS_UNSPECIFIED: 0,
  NODE_STATUS_ACTIVE: 1,
  NODE_STATUS_INACTIVE: 2,
  NODE_STATUS_MAINTENANCE: 3,
  NODE_STATUS_REGISTRATION_STARTED: "NODE_STATUS_REGISTRATION_STARTED",
  NODE_STATUS_REGISTRATION_FAILED: "NODE_STATUS_REGISTRATION_FAILED"
};

export const transformNodeToDisplayFormat = (node) => {
  if (!node) return null;
  
  const getStatusText = (status) => {
    switch (status) {
      case NodeStatus.NODE_STATUS_ACTIVE:
      case "NODE_STATUS_ACTIVE":
      case 1: 
        return 'Active';
      case NodeStatus.NODE_STATUS_INACTIVE:
      case "NODE_STATUS_INACTIVE":
      case 2: 
        return 'Offline';
      case NodeStatus.NODE_STATUS_MAINTENANCE:
      case "NODE_STATUS_MAINTENANCE":
      case 3: 
        return 'Maintenance';
      case NodeStatus.NODE_STATUS_REGISTRATION_STARTED:
      case "NODE_STATUS_REGISTRATION_STARTED":
        return 'Registering';
      case NodeStatus.NODE_STATUS_REGISTRATION_FAILED:
      case "NODE_STATUS_REGISTRATION_FAILED":
        return 'Registration Failed';
      default: 
        return 'Unknown';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case NodeType.NODE_TYPE_COMPUTE: return 'Compute';
      case NodeType.NODE_TYPE_STORAGE: return 'Storage';
      default: return 'Unknown';
    }
  };

  const getCpuModel = (nodeMetadata) => {
    return nodeMetadata?.cpu_info?.model || 'Unknown CPU';
  };

  const getMemoryInfo = (nodeMetadata) => {
    const memoryTotal = nodeMetadata?.memory_info?.total;
    if (memoryTotal) {
      const numericValue = parseFloat(memoryTotal.replace(/[^\d.]/g, ''));
      return Math.round(numericValue);
    }
    return node.memory || 0;
  };

  const getStorageInfo = (nodeMetadata) => {
    if (nodeMetadata?.storage_info?.disks?.length > 0) {
      const totalSize = nodeMetadata.storage_info.disks.reduce((total, disk) => {
        const sizeMatch = disk.size?.match(/(\d+\.?\d*)/);
        return total + (sizeMatch ? parseFloat(sizeMatch[1]) : 0);
      }, 0);
      return Math.round(totalSize);
    }
    return node.storage || 0;
  };

  const getPublicIpAddress = (nodeMetadata) => {
    return nodeMetadata?.network_info?.addresses?.public || 
           nodeMetadata?.network_info?.interfaces?.find(iface => 
             iface.ip_addresses?.some(ip => !ip.startsWith('192.168') && !ip.startsWith('10.') && !ip.startsWith('172.') && !ip.includes(':'))
           )?.ip_addresses?.find(ip => !ip.startsWith('192.168') && !ip.startsWith('10.') && !ip.startsWith('172.') && !ip.includes(':')) || 
           'Unknown';
  };

  const getHostname = (nodeMetadata) => {
    return nodeMetadata?.system_info?.hostname || `node-${node.id?.substring(0, 8)}`;
  };

  const getUptimeInfo = (nodeMetadata) => {
    return nodeMetadata?.uptime_info?.human || 'Unknown';
  };

  const getClockSpeed = (nodeMetadata) => {
    const clockSpeed = nodeMetadata?.cpu_info?.clock_speed;
    if (clockSpeed) {
      return clockSpeed.replace(/(\d+\.?\d*)\s*MHz/, '$1 MHz');
    }
    return 'Unknown';
  };

  const isRegistering = (status) => {
    return status === "NODE_STATUS_REGISTRATION_STARTED" || status === NodeStatus.NODE_STATUS_REGISTRATION_STARTED;
  };

  const isRegistrationFailed = (status) => {
    return status === "NODE_STATUS_REGISTRATION_FAILED" || status === NodeStatus.NODE_STATUS_REGISTRATION_FAILED;
  };

  return {
    id: node.id,
    nodeId: node.id,
    hostname: getHostname(node.nodeMetadata),
    ipAddress: getPublicIpAddress(node.nodeMetadata),
    port: node.address_details?.port || node.addressDetails?.port || '22',
    region: node.address_details?.region || node.addressDetails?.region || 'Unknown',
    location: node.address_details?.region || node.addressDetails?.region || 'Unknown',
    status: getStatusText(node.status),
    rawStatus: node.status,
    isRegistering: isRegistering(node.status),
    isRegistrationFailed: isRegistrationFailed(node.status),
    type: getTypeText(node.type),
    singleNode: node.single_node || node.singleNode || false,
    slurmHead: node.slurm_head || node.slurmHead || false,
    slurmOwner: node.slurm_owner || node.slurmOwner || '',
    numCpus: node.num_of_cpus || node.numOfCpus || 0,
    numGpus: node.num_of_gpus || node.numOfGpus || 0,
    memory: getMemoryInfo(node.nodeMetadata),
    storage: getStorageInfo(node.nodeMetadata),
    os: node.os || node.nodeMetadata?.os_info?.name || 'Unknown',
    osVersion: node.os_version || node.osVersion || node.nodeMetadata?.os_info?.version || '',
    kernelVersion: node.kernel_version || node.kernelVersion || node.nodeMetadata?.os_info?.kernel || '',
    gpus: node.gpus || [],
    nodeMetadata: node.node_metadata || node.nodeMetadata,
    createdAt: node.created_at || node.createdAt,
    updatedAt: node.updated_at || node.updatedAt,
    specification: `${node.num_of_cpus || node.numOfCpus || 0} CPUs, ${getMemoryInfo(node.nodeMetadata)}GB RAM`,
    function: node.slurm_head || node.slurmHead ? "Slurm Head Node" : getTypeText(node.type) + " Node",
    health: isRegistrationFailed(node.status) ? "Registration Failed" : "Good",
    coordinates: [0, 0],
    machineId: node.id,
    gpuType: node.gpus && node.gpus.length > 0 ? node.gpus[0].model : "N/A",
    gpu: node.gpus && node.gpus.length > 0 ? getGpuVendorText(node.gpus[0].vendor) : "None",
    vram: calculateTotalVRAM(node.gpus),
    cpuModel: getCpuModel(node.nodeMetadata),
    clockSpeed: getClockSpeed(node.nodeMetadata),
    uptime: getUptimeInfo(node.nodeMetadata),
    cpuCores: node.nodeMetadata?.cpu_info?.cores || node.num_of_cpus || node.numOfCpus || 0,
    cpuThreads: node.nodeMetadata?.cpu_info?.threads || 0,
    architecture: node.nodeMetadata?.os_info?.architecture || 'Unknown'
  };
};

const getGpuVendorText = (vendor) => {
  switch (vendor) {
    case 1: return 'NVIDIA';
    case 2: return 'AMD';
    case 3: return 'Intel';
    default: return 'Unknown';
  }
};

const calculateTotalVRAM = (gpus) => {
  if (!gpus || gpus.length === 0) return 0;
  return gpus.length * 8;
};

export const createNodeRequest = (formData) => {
  return {
    addressDetails: {
      ipAddress: formData.ipAddress,
      port: formData.port,
      region: formData.region
    },
    nodeType: parseInt(formData.nodeType) || NodeType.NODE_TYPE_COMPUTE,
    sudoUserCreds: {
      username: formData.username,
      password: formData.password,
      privateKey: formData.privateKey
    },
    clientId: formData.clientId,
    departmentIds: formData.departmentIds || [],
    teamIds: formData.teamIds || [],
    userIds: formData.userIds || []
  };
};

const handleError = (error) => {
  console.error('Node API Error:', error);
  
  if (error.response?.status === 503) {
    return {
      success: false,
      error: 'Service temporarily unavailable'
    };
  }

  if (error.response?.status === 401) {
    return {
      success: false,
      error: 'Unauthorized - Invalid credentials'
    };
  }

  if (error.response?.status === 400) {
    return {
      success: false,
      error: error.response?.data?.message || 'Bad request - Invalid data provided'
    };
  }
  
  return {
    success: false,
    error: error.response?.data?.message || error.message || 'Operation failed'
  };
}; 